import { call, all, put } from 'redux-saga/effects';
import { get } from 'lodash';
import ViewerApi from 'src/apis/viewer';
import appDux from 'src/redux/app';
import macheteDux from 'src/redux/machete';
import utils from 'src/utils';
import constants from 'src/constants';
import featureFlags from 'src/featureFlags';
import GraphQLApi from 'src/apis/GraphQLApi';

const { MATERIALS } = constants.labels;
const { convertArrayToMapById, convertArrayToMapByIdRecursively } = utils.api;
const { addRepeatablePropertyToProducts } = utils.machete;
const { isPaintableMaterial, getDefaultTextureForLabel } = utils.productCatalog;
const { parseProductsAndVariations } = utils.texturing;

/* DATA MANIPULATION UTILS */

const addTextureDataToVariations = (variations, products) =>
  variations.map((variation) => {
    const textureId = variation.product_id;
    return {
      ...variation,
      texture:
        textureId && products[textureId] ? products[textureId].texture : null,
      textureData:
        textureId && products[textureId] ? products[textureId] : null,
    };
  });

const addVariationDataToProducts = (
  updatedMacheteVariations,
  updatedMacheteVariationGroups,
  macheteProductsMap,
  products,
) =>
  [updatedMacheteVariations, updatedMacheteVariationGroups].forEach((array) =>
    array.forEach((variation) => {
      const macheteProduct = macheteProductsMap[variation.parent_id];

      if (macheteProduct && variation.manufacturer_id) {
        const textureProduct = products[macheteProduct.product_id];
        macheteProduct.texture = textureProduct ? textureProduct.texture : null;
        macheteProduct.textureData = textureProduct || null;
        macheteProduct.label_id = get(
          products,
          `${macheteProduct.product_id}.label_id`,
        );
        macheteProduct.manufacturer_ids =
          macheteProduct.manufacturer_ids || new Set();
        macheteProduct.manufacturer_ids.add(variation.manufacturer_id);
        macheteProduct.hasColors = true;
      }
    }),
  );

const addLabelIdsToManufacturers = (macheteProducts, manufacturers) =>
  macheteProducts.forEach((macheteProduct) => {
    const manufacturer = manufacturers[macheteProduct.manufacturer_id];
    const hasValidLabelId = macheteProduct.label_id >= 0;

    if (manufacturer && hasValidLabelId) {
      manufacturer.label_ids = manufacturer.label_ids || new Set();
      manufacturer.label_ids.add(macheteProduct.label_id);
      manufacturer.hasProducts = true;
    }
  });

const addProductDataToLabels = (products, labels, manufacturers) => {
  const productsArr = Object.values(products);
  Object.values(labels).forEach((label) => {
    const manufacturer = Object.values(manufacturers).find(
      (m) => m.label_ids && m.label_ids.has(label.id),
    );
    const product = getDefaultTextureForLabel(label, productsArr);

    label.category = MATERIALS[label.id] ? MATERIALS[label.id].category : null;
    label.hasBrands = !!manufacturer || isPaintableMaterial(label.id);
    label.defaultProductId = product ? product.id : null;
    label.imageUrl = get(product, 'texture.thumbnail.url', null);
    label.textureData = product;
  });
};

const addManufacturerProductDataToProducts = (manufacturerProducts, products) =>
  Object.values(manufacturerProducts).forEach((mp) => {
    const product = products[mp.parent_id];

    if (product && mp.manufacturer_id) {
      product.manufacturer_ids = product.manufacturer_ids || new Set();
      product.manufacturer_ids.add(mp.manufacturer_id);
      product.hasColors = true;
    }
  });

/* INITIALIZATION */

export default function* initializeProductCatalogDataSaga({ job }) {
  const [
    { products },
    { data: manufacturersArr },
    { data: manufacturerProductsApiRes },
    { data: labelsArr },
  ] = yield call(fetchProductCatalogDataSaga, { job });
  yield call(
    transformDesignProData,
    products,
    manufacturersArr,
    manufacturerProductsApiRes,
    labelsArr,
  );
  yield put(appDux.actions.setProductCatalogInitialized());
}

/* API CALLS */

function* fetchJobProductsSaga({ job }) {
  const { data: products } = yield call(
    [ViewerApi, ViewerApi.getProducts],
    job,
  );
  let productsMap = convertArrayToMapByIdRecursively(products);
  productsMap = addRepeatablePropertyToProducts(productsMap);

  yield put(macheteDux.actions.receiveProducts(productsMap));

  return { products: productsMap };
}

function* fetchProductCatalogDataSaga({ job }) {
  return yield all([
    call(fetchJobProductsSaga, { job }),
    call([ViewerApi, ViewerApi.getManufacturers], job),
    call([ViewerApi, ViewerApi.getManufacturerProducts], job),
    call([ViewerApi, ViewerApi.getLabels]),
  ]);
}

/* SIDE EFFECTS */

function* transformDesignProData(
  products,
  manufacturersArr,
  manufacturerProductsApiRes,
  labelsArr,
) {
  const labels = convertArrayToMapByIdRecursively(labelsArr);
  const manufacturers = convertArrayToMapByIdRecursively(manufacturersArr);
  const manufacturerProducts = convertArrayToMapByIdRecursively(
    manufacturerProductsApiRes,
  );
  const {
    variations: macheteVariations,
    variationGroups: macheteVariationGroups,
    products: macheteProducts,
  } = parseProductsAndVariations(manufacturerProductsApiRes);

  const updatedMacheteVariations = addTextureDataToVariations(
    macheteVariations,
    products,
  );

  const updatedMacheteVariationGroups = macheteVariationGroups.map(
    (variationGroup) => ({
      ...variationGroup,
      children: variationGroup.children.map((variation) => ({
        ...variation,
        texture:
          variation.product_id && products[variation.product_id]
            ? products[variation.product_id].texture
            : null,
      })),
    }),
  );

  const macheteProductsMap = convertArrayToMapById(macheteProducts);
  addVariationDataToProducts(
    updatedMacheteVariations,
    updatedMacheteVariationGroups,
    macheteProductsMap,
    products,
  );

  addManufacturerProductDataToProducts(manufacturerProducts, products);
  addLabelIdsToManufacturers(macheteProducts, manufacturers);
  addProductDataToLabels(products, labels, manufacturers);

  // receive all the modified data into the store
  yield put(
    appDux.actions.receiveMacheteData({
      products: convertArrayToMapById(macheteProducts),
      variations: convertArrayToMapById(updatedMacheteVariations),
      variationGroups: convertArrayToMapById(updatedMacheteVariationGroups),
    }),
  );
  yield put(macheteDux.actions.receiveProducts(products));
  yield put(macheteDux.actions.receiveLabels(labels));
  yield put(macheteDux.actions.receiveManufacturers(manufacturers));
  yield put(
    macheteDux.actions.receiveManufacturerProducts(manufacturerProducts),
  );
}
